import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ListingCard, SaveHomeButton, perUnit, posterLabel, type PublicListing } from '../components/ListingCard';
import { recordListingView } from '../lib/visitor';
import { ListingMedia } from '../components/ListingMedia';
import { useApi } from '../lib/useApi';
import { usePortal } from '../lib/portal';
import { apiRequest, ApiError } from '../lib/api';
import { money, shortDate } from '../lib/format';
import {
  Alert, Button, ErrorBlock, Field, LoadingBlock, TextInput,
} from '../components/ui';
import type { VideoChapter } from '../components/Video360';
import '../styles/public.css';
import '../styles/discover.css';

/**
 * One home, in full.
 *
 * The enquiry form is the point of the page, so it stays beside the gallery on
 * a desktop and directly under the facts on a phone — never below a wall of
 * description where nobody scrolls to it.
 */

interface Detail extends PublicListing {
  video_projection?: string | null;
  video_chapters?: VideoChapter[] | null;
  contact_preference: string;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
}
/** "today", "yesterday", "5 days ago" — how fresh a confirmation is, in words. */
function sinceWords(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

interface Response {
  listing: Detail;
  similar: PublicListing[];
  amenities: { code: string; label: string }[];
}

function Enquiry({ listingId, contactName }: { listingId: string; contactName: string | null }) {
  const [form, setForm] = useState({
    name: '', phone: '', email: '', message: '', wantsViewing: false, preferredViewingDate: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true); setError(null);
    try {
      const { preferredViewingDate, ...rest } = form;
      await apiRequest(`/api/v1/discover/${listingId}/enquiries`, {
        method: 'POST',
        body: {
          ...rest,
          phone: rest.phone || undefined, email: rest.email || undefined,
          message: rest.message || undefined,
          ...(form.wantsViewing && preferredViewingDate ? { preferredViewingDate } : {}),
        },
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="dsc-enquiry">
        <Alert tone="success">
          Sent. {contactName ?? 'The landlord'} has your details and will be in touch
          {form.wantsViewing ? ' to arrange a viewing' : ''}.
        </Alert>
      </div>
    );
  }

  return (
    <form className="dsc-enquiry" onSubmit={submit}>
      <h2>Enquire about this home</h2>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Your name" htmlFor="e-name">
        <TextInput id="e-name" required value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Phone" htmlFor="e-phone" hint="A phone number or an email — either is enough.">
        <TextInput id="e-phone" inputMode="tel" value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label="Email" htmlFor="e-email">
        <TextInput id="e-email" type="email" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} />
      </Field>
      <label className="dsc-viewing-check">
        <input type="checkbox" checked={form.wantsViewing}
          onChange={e => setForm({ ...form, wantsViewing: e.target.checked })} />{' '}
        I would like to view this home
      </label>
      {form.wantsViewing && (
        <Field label="Preferred day (optional)" htmlFor="e-view-date"
          hint="The landlord will confirm an actual time with you.">
          <TextInput id="e-view-date" type="date" value={form.preferredViewingDate}
            min={new Date().toISOString().slice(0, 10)}
            onChange={e => setForm({ ...form, preferredViewingDate: e.target.value })} />
        </Field>
      )}
      <Field label="Message" htmlFor="e-msg">
        <textarea id="e-msg" className="input" rows={3}
          placeholder="Is it still available? When could I view it?"
          value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
      </Field>
      <Button type="submit" block loading={sending}>Send enquiry</Button>
    </form>
  );
}

/**
 * Asking for dates at a short stay (155). A request, not a booking: the host
 * confirms, or the guest books on the host's own Airbnb page.
 */
function StayRequest({ listingId, contactName, minNights, maxGuests }: {
  listingId: string; contactName: string | null; minNights: number | null; maxGuests: number | null;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ name: '', phone: '', email: '', stayFrom: '', stayTo: '', guests: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nights = form.stayFrom && form.stayTo
    ? Math.round((Date.parse(form.stayTo) - Date.parse(form.stayFrom)) / 86_400_000) : 0;
  const tooShort = !!minNights && nights > 0 && nights < minNights;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true); setError(null);
    try {
      await apiRequest(`/api/v1/discover/${listingId}/enquiries`, {
        method: 'POST',
        body: {
          name: form.name, phone: form.phone || undefined, email: form.email || undefined,
          message: form.message || undefined,
          stayFrom: form.stayFrom || undefined, stayTo: form.stayTo || undefined,
          guests: form.guests ? Number(form.guests) : undefined,
        },
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="dsc-enquiry">
        <Alert tone="success">
          Sent. {contactName ?? 'The host'} has your dates and will confirm whether they are free.
        </Alert>
      </div>
    );
  }

  return (
    <form className="dsc-enquiry" onSubmit={submit}>
      <h2>Request these dates</h2>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="grid grid-2">
        <Field label="Check-in" htmlFor="s-from">
          <TextInput id="s-from" type="date" min={today} required value={form.stayFrom}
            onChange={e => setForm({ ...form, stayFrom: e.target.value })} />
        </Field>
        <Field label="Check-out" htmlFor="s-to">
          <TextInput id="s-to" type="date" min={form.stayFrom || today} required value={form.stayTo}
            onChange={e => setForm({ ...form, stayTo: e.target.value })} />
        </Field>
      </div>
      {nights > 0 && <p className="muted small" style={{ marginTop: 0 }}>{nights} night{nights === 1 ? '' : 's'}</p>}
      {tooShort && <Alert tone="warning">The minimum stay here is {minNights} nights.</Alert>}
      <Field label="Guests" htmlFor="s-guests" hint={maxGuests ? `Sleeps up to ${maxGuests}.` : undefined}>
        <TextInput id="s-guests" type="number" min={1} max={maxGuests ?? 50} value={form.guests}
          onChange={e => setForm({ ...form, guests: e.target.value })} />
      </Field>
      <Field label="Your name" htmlFor="s-name">
        <TextInput id="s-name" required value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Phone" htmlFor="s-phone" hint="A phone number or an email, either is enough.">
        <TextInput id="s-phone" inputMode="tel" value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label="Email" htmlFor="s-email">
        <TextInput id="s-email" type="email" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} />
      </Field>
      <Field label="Message" htmlFor="s-msg">
        <textarea id="s-msg" className="input" rows={3} placeholder="Arrival time, anything the host should know"
          value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
      </Field>
      <Button type="submit" block loading={sending} disabled={tooShort}>Send request</Button>
    </form>
  );
}

/**
 * Applying to rent it (132).
 *
 * Kept behind a disclosure rather than shown beside the enquiry, because most
 * people on this page are still deciding and a nine-field form is a way to lose
 * them. The enquiry stays the first thing offered; this is for the person who
 * has already made their mind up.
 *
 * WHAT IT DOES NOT ASK FOR is the design: no ID number, no payslip, no upload.
 * The hint under the income box says the figure is not checked, because a
 * landlord reading it later will otherwise assume somebody did.
 */
function Apply({ listingId, contactName }: { listingId: string; contactName: string | null }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', householdSize: '', occupation: '',
    employer: '', monthlyIncome: '', moveInDate: '', message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true); setError(null);
    try {
      await apiRequest(`/api/v1/discover/${listingId}/applications`, {
        method: 'POST',
        body: {
          name: form.name,
          phone: form.phone || undefined,
          email: form.email || undefined,
          householdSize: form.householdSize ? Number(form.householdSize) : undefined,
          occupation: form.occupation || undefined,
          employer: form.employer || undefined,
          monthlyIncome: form.monthlyIncome || undefined,
          moveInDate: form.moveInDate || undefined,
          message: form.message || undefined,
        },
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="dsc-enquiry">
        <Alert tone="success">
          Your application is with {contactName ?? 'the landlord'}. They will contact you
          directly — NyumbaSuite does not decide who gets the home.
        </Alert>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="dsc-enquiry">
        <h2>Ready to move?</h2>
        <p className="muted">
          Apply for this home and the landlord gets what they need to consider you:
          when you want to move, who would live there and how to reach you.
        </p>
        <Button type="button" variant="secondary" block onClick={() => setOpen(true)}>
          Apply to rent this home
        </Button>
      </div>
    );
  }

  return (
    <form className="dsc-enquiry" onSubmit={submit}>
      <h2>Apply to rent this home</h2>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="Your name" htmlFor="a-name">
        <TextInput id="a-name" required value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Phone" htmlFor="a-phone" hint="A phone number or an email — either is enough.">
        <TextInput id="a-phone" inputMode="tel" value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })} />
      </Field>
      <Field label="Email" htmlFor="a-email">
        <TextInput id="a-email" type="email" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} />
      </Field>
      <Field label="When would you move in?" htmlFor="a-move">
        <TextInput id="a-move" type="date" value={form.moveInDate}
          min={new Date().toISOString().slice(0, 10)}
          onChange={e => setForm({ ...form, moveInDate: e.target.value })} />
      </Field>
      <Field label="How many people would live here?" htmlFor="a-house">
        <TextInput id="a-house" type="number" min={1} max={30} value={form.householdSize}
          onChange={e => setForm({ ...form, householdSize: e.target.value })} />
      </Field>
      <Field label="What do you do?" htmlFor="a-occ">
        <TextInput id="a-occ" value={form.occupation}
          onChange={e => setForm({ ...form, occupation: e.target.value })} />
      </Field>
      <Field label="Employer or business" htmlFor="a-emp">
        <TextInput id="a-emp" value={form.employer}
          onChange={e => setForm({ ...form, employer: e.target.value })} />
      </Field>
      <Field label="Monthly income (optional)" htmlFor="a-inc"
        hint="Nobody checks this figure — it is shown to the landlord as something you told them.">
        <TextInput id="a-inc" inputMode="numeric" value={form.monthlyIncome}
          onChange={e => setForm({ ...form, monthlyIncome: e.target.value })} />
      </Field>
      <Field label="Anything else?" htmlFor="a-msg">
        <textarea id="a-msg" className="input" rows={3}
          value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
      </Field>
      {/* Said before they press, not after. */}
      <p className="muted" style={{ marginBottom: 12 }}>
        This goes to the landlord only. We never ask for an ID card, a payslip or a
        bank statement, and there is nowhere here to attach one.
      </p>
      <Button type="submit" block loading={sending}>Send application</Button>
    </form>
  );
}

export function ListingDetail() {
  const { id } = useParams();
  const { siteHome } = usePortal();
  const view = useApi<Response>(id ? `/api/v1/discover/${id}` : null);
  // Counted once the page has a listing to count, and once per visit.
  const loadedId = view.data?.listing.id;
  useEffect(() => { if (loadedId) recordListingView(loadedId); }, [loadedId]);

  if (view.loading) return <><PublicHeader /><div className="page"><LoadingBlock /></div><SiteFooter /></>;
  if (view.error) {
    // A withdrawn or let home is the common case here, not a fault — say so
    // plainly and offer the way back to the search.
    return (
      <>
        <PublicHeader />
        <div className="page dsc-gone">
          {view.error.status === 404 ? (
            <>
              <h1>That home is no longer listed</h1>
              <p className="page-sub">It may have been let or withdrawn.</p>
              <Link className="btn btn-primary" to="/homes">Back to search</Link>
            </>
          ) : <ErrorBlock error={view.error} onRetry={view.reload} />}
        </div>
        <SiteFooter />
      </>
    );
  }

  const l = view.data!.listing;
  const photos = l.photo_ids ?? [];
  const labels = new Map(view.data!.amenities.map(a => [a.code, a.label]));
  const where = [l.estate_area, l.county].filter(Boolean).join(', ');

  return (
    <>
      <PublicHeader />

      <div className="dsc-detail">
        <nav className="dsc-crumbs" aria-label="Breadcrumb">
          <Link to={siteHome}>Home</Link>
          <span aria-hidden> › </span>
          <Link to="/homes">Find your next home</Link>
          <span aria-hidden> › </span>
          <span>{where || l.title}</span>
        </nav>

        <div className="dsc-detail-grid">
          <div>
            <ListingMedia
              title={l.title}
              photoIds={photos}
              panoramas={l.panoramas ?? []}
              videoId={l.video_id ?? null}
              videoProjection={l.video_projection ?? null}
              videoChapters={l.video_chapters ?? null}
            />

            {/* Above the title, not beside the price: someone who arrived here
                from a search result needs to learn this before they read the
                rent and start planning around it. */}
            {l.is_sold_out && (
              <p className="dsc-taken" role="status">
                <strong>This home has been taken.</strong> It is no longer
                available to rent, and enquiries are closed. Browse the other
                homes below.
              </p>
            )}

            <header className="dsc-head">
              {/* Paid placement is labelled here as on the card that led here. */}
              {l.is_featured && !l.is_sold_out && <span className="lst-featured">Featured</span>}
              <div className="dsc-title-row">
                <h1>{l.title}</h1>
                <SaveHomeButton id={l.id} className="dsc-save" />
              </div>
              {where && <p className="dsc-where">{where}</p>}
              {l.stay_type === 'short_stay' && (
                <span className="lst-featured lst-short">Short stay{l.tourism_licence ? ` · Licensed ${l.tourism_licence}` : ''}</span>
              )}
              <p className="dsc-price">
                {money(l.rent)}<span className="lst-per">{perUnit(l)}</span>
                {l.stay_type === 'short_stay' && l.weekly_rate && (
                  <span className="dsc-deposit">{money(l.weekly_rate)}/week</span>
                )}
                {l.stay_type === 'short_stay' && l.cleaning_fee && Number(l.cleaning_fee) > 0 && (
                  <span className="dsc-deposit">+ {money(l.cleaning_fee)} cleaning fee</span>
                )}
                {l.stay_type !== 'short_stay' && l.deposit && <span className="dsc-deposit">{money(l.deposit)} deposit</span>}
                {l.stay_type !== 'short_stay' && l.service_charge && Number(l.service_charge) > 0 && (
                  <span className="dsc-deposit">+ {money(l.service_charge)}/month service charge</span>
                )}
              </p>
              {l.availability_confirmed_at && !l.is_sold_out && (
                <p className="dsc-fresh">
                  ✓ Availability confirmed by the landlord {sinceWords(l.availability_confirmed_at)}
                </p>
              )}
            </header>

            <dl className="dsc-facts">
              {l.bedrooms !== null && <div><dt>Bedrooms</dt><dd>{l.bedrooms}</dd></div>}
              {l.bathrooms !== null && <div><dt>Bathrooms</dt><dd>{l.bathrooms}</dd></div>}
              {l.unit_type && <div><dt>Type</dt><dd>{l.unit_type}</dd></div>}
              <div>
                <dt>Available</dt>
                <dd>{l.available_from ? shortDate(l.available_from) : 'Now'}</dd>
              </div>
              {l.utilities_note && <div><dt>Utilities</dt><dd>{l.utilities_note}</dd></div>}
              {l.stay_type === 'short_stay' && l.max_guests && <div><dt>Sleeps</dt><dd>{l.max_guests}</dd></div>}
              {l.stay_type === 'short_stay' && l.min_nights && <div><dt>Minimum stay</dt><dd>{l.min_nights} night{l.min_nights === 1 ? '' : 's'}</dd></div>}
              {l.stay_type === 'short_stay' && l.check_in_time && <div><dt>Check-in</dt><dd>{l.check_in_time}</dd></div>}
              {l.stay_type === 'short_stay' && l.check_out_time && <div><dt>Check-out</dt><dd>{l.check_out_time}</dd></div>}
            </dl>

            {l.stay_type === 'short_stay' && l.house_rules && (
              <section className="dsc-section">
                <h2>House rules</h2>
                <p className="dsc-desc">{l.house_rules}</p>
              </section>
            )}

            {l.description && (
              <section className="dsc-section">
                <h2>About this home</h2>
                <p className="dsc-desc">{l.description}</p>
              </section>
            )}

            {l.amenities.length > 0 && (
              <section className="dsc-section">
                <h2>Amenities</h2>
                <ul className="dsc-amenities">
                  {l.amenities.map(a => (
                    <li key={a}><span aria-hidden>✓</span> {labels.get(a) ?? a}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          <aside className="dsc-side">
            {/* A taken unit publishes no way to be contacted about it. The
                server refuses the enquiry either way; hiding the form and the
                phone number is what stops the wasted call being made at all. */}
            {l.is_sold_out ? (
              <div className="dsc-contact">
                <h2>Enquiries closed</h2>
                <p className="muted">
                  This home has been taken, so it is not accepting enquiries.
                </p>
                <Link to="/homes" className="btn btn-secondary" style={{ marginTop: 12 }}>
                  See homes still available
                </Link>
              </div>
            ) : (
              <>
                <div className="dsc-contact">
                  <h2>Contact</h2>
                  <p className="dsc-contact-name">{l.contact_name ?? 'The landlord'}</p>
                  {posterLabel(l.poster_role) && <p className="muted small" style={{ marginTop: 0 }}>{posterLabel(l.poster_role)}</p>}
                  {/* Only what the landlord chose to publish reaches this page — the
                      server withholds the rest rather than the interface hiding it. */}
                  {l.contact_phone && <p><a href={`tel:${l.contact_phone}`}>{l.contact_phone}</a></p>}
                  {l.contact_email && <p><a href={`mailto:${l.contact_email}`}>{l.contact_email}</a></p>}
                  {!l.contact_phone && !l.contact_email && (
                    <p className="muted">Use the form to get in touch.</p>
                  )}
                </div>
                {l.stay_type === 'short_stay' ? (
                  <>
                    {/* The host's own page on Airbnb or Booking.com. The server
                        only stores links to those sites (155). */}
                    {l.booking_url && (
                      <div className="dsc-contact">
                        <a className="btn btn-primary btn-block" href={l.booking_url}
                          target="_blank" rel="noopener noreferrer nofollow">
                          Book on {/booking\.com/i.test(l.booking_url) ? 'Booking.com'
                            : /vrbo\.com/i.test(l.booking_url) ? 'Vrbo' : 'Airbnb'}
                        </a>
                        <p className="muted small" style={{ marginBottom: 0 }}>Opens the host's own page. Or ask the host for your dates below.</p>
                      </div>
                    )}
                    <StayRequest listingId={l.id} contactName={l.contact_name}
                      minNights={l.min_nights ?? null} maxGuests={l.max_guests ?? null} />
                  </>
                ) : (
                  <>
                    <Enquiry listingId={l.id} contactName={l.contact_name} />
                    <Apply listingId={l.id} contactName={l.contact_name} />
                  </>
                )}
              </>
            )}
          </aside>
        </div>

        {view.data!.similar.length > 0 && (
          <section className="dsc-section dsc-similar">
            <h2>{l.stay_type === 'short_stay' ? 'Similar places to stay' : 'Similar homes'}</h2>
            <div className="dsc-grid">
              {view.data!.similar.map(s => <ListingCard key={s.id} listing={s} />)}
            </div>
          </section>
        )}
      </div>

      <SiteFooter />
    </>
  );
}
